import { FC, useContext, useEffect, useRef } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { useCountTo } from "../../hooks/useCountTo";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminClaimsApi } from "../../http/claims/api";
import { formatNumber } from "../../types/utils";
import styles from "../cars/CarStats.module.css";

const ClaimStats: FC = () => {
  const i18n = useI18n();
  const claimsApi = useAdminClaimsApi();
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);

  const statsResult = claimsApi.useClaimStats(aborter.current.signal);

  useEffect(() => {
    statsResult.error && dispatch(showErrorNotification(i18n, statsResult.error));
  }, [statsResult.error]);

  const stats = [
    {
      number: statsResult.data?.totalOpen,
      title: i18n.t.common.open,
      color: "#b3ffc2",
      borderColor: "#4aa55c80",
    },
    {
      number: statsResult.data?.totalInProgress,
      title: i18n.t.common.inProgress,
      color: "#ffffad",
      borderColor: "#9b9b5280",
    },
    {
      number: statsResult.data?.totalAwaitingDocuments,
      title: i18n.t.common.awaitingDocuments,
      color: "#ffdd83",
      borderColor: "#9b854c50",
    },
  ];

  return (
    <div className={styles["stats-container"]}>
      {stats.map((stat) => (
        <StatCard
          key={stat.title}
          number={stat.number}
          title={stat.title}
          color={stat.color}
          borderColor={stat.borderColor}
          isLoading={!statsResult.data}
          language={i18n.language}
        />
      ))}
    </div>
  );
};

export default ClaimStats;

type StatCardProps = {
  number?: number;
  title: string;
  color: string;
  borderColor: string;
  isLoading: boolean;
  language: string;
};

const StatCard: FC<StatCardProps> = ({ number, title, color, borderColor, isLoading, language }) => {
  const animatedNumber = useCountTo({ target: number, isReady: !isLoading });

  return (
    <div className={styles["stat-card"]} style={{ backgroundColor: color, borderColor }}>
      <div className={styles["number-container"]}>
        <div className={styles["stat-number"]}>{formatNumber(animatedNumber, language, { fractionalDigits: 0 })}</div>
      </div>
      <div className={styles["stat-title"]}>{title}</div>
    </div>
  );
};
